import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Center, Column, Screen, Tab, TabPanel, Tabs } from '@tempmee/react';
import { CenteredSpinner, BackButton } from '~/commonComponents';
import { DetailsPageFrame } from '~/components/shared/DetailsPage';
import { NoScreen } from '~/components/shared/NoScreen';
import { DebOfficeUpdates } from '~/constants/ff.posthog';
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { useBlockBackNavigation, useJobProfessionDisplay } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { ProfessionTypeEnum } from '~/models';
import {
  CandidateTabView,
  CloseJobFeedbackPopup,
  CloseJobSuccessPopup,
  ConfirmCloseJobPopup,
  JobDetailsTabView,
  JobOverviewHeader,
  OnsiteInterviewTabView,
  SelectCandidatesPopup,
  useCloseJobFlow,
} from './components';
import { TJobOverviewPageRoutes } from './Types';
import { TAB, useJobOverview } from './useJobOverview';
import { useJobOverviewData } from './useJobOverviewData';
import { useSafeLocationState } from './useSafeLocationState';
import { CreateOnsiteInterviewSteps } from '../CreateOnsiteInterview';
import { CreatePermanentJobPostingSteps } from '../CreatePosting';
import { PostingDetails } from '../PostingDetails';
import { POSTING_DETAILS_COPY } from '../PostingDetails/postingDetailsRules';
import { invalidatePostingLists } from '../utils';

const BLOCKED_ROUTE_PATTERNS = [
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.BasicInformation}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.JobDescription}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.ReviewAndPayment}`,
  `/permanent-jobs/postings/:id/create/${CreatePermanentJobPostingSteps.WorkDetails}`,
  `/permanent-jobs/postings/:id/create`,
  `/permanent-jobs/postings/:id/onsite-interviews/create/${CreateOnsiteInterviewSteps.Reviews}`,

  `/permanent-jobs/postings/:id/onsite-interviews/create/${CreateOnsiteInterviewSteps.WorkDetails}`,
  `/permanent-jobs/postings/:id/onsite-interviews/create`,
];

export const JobOverview = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { postingId } = useParams<TJobOverviewPageRoutes>();

  const { activeTab, onChangeTab } = useJobOverview();
  const isPostingDetails = useFeatureFlag(DebOfficeUpdates);

  const {
    jobPosting,
    hasCandidatesToHire,
    hasCandidateScheduledOnsiteInterviews,
    hasScheduledOnsiteInterviews,
    isLoading,
  } = useJobOverviewData(postingId);

  const {
    currentStep: closeJobStep,
    selectedCandidateIds,
    hasHires,
    isClosingJob,
    startFlow: startCloseJobFlow,
    handleSelectCandidates,
    handleGoBack,
    handleConfirmClose,
    handleFeedbackSkip,
    handleFeedbackSubmit,
    closeFlow: endCloseJobFlow,
  } = useCloseJobFlow({ postingId, hasCandidatesToHire });
  const {
    fromOnsiteInterviewCreation,
    fromPostingDetails,
    startCloseJobFlow: shouldAutoStartCloseJobFlow,
  } = useSafeLocationState();

  // When the office chose "Close Posting" from the Active card's ⋯ menu, open the
  // Close Job flow automatically once the posting has loaded — but only once.
  const hasAutoStartedCloseJobFlow = useRef(false);
  useEffect(() => {
    if (
      shouldAutoStartCloseJobFlow &&
      !isLoading &&
      jobPosting?.status === JobPostingStatus.Active &&
      !hasAutoStartedCloseJobFlow.current
    ) {
      hasAutoStartedCloseJobFlow.current = true;
      startCloseJobFlow();
    }
  }, [shouldAutoStartCloseJobFlow, isLoading, jobPosting?.status, startCloseJobFlow]);

  const { title, professionType, subProfession } = jobPosting || {};

  const professionDisplay = useJobProfessionDisplay({
    profession: professionType || ProfessionTypeEnum.RDH,
    subProfession,
  });

  const handleCloseJobSuccess = () => {
    endCloseJobFlow();
    // Invalidate query to refetch latest data in parent component
    queryClient.invalidateQueries({
      queryKey: ['GET_JOB_POSTING_DETAIL', postingId],
    });
    // The page stays mounted after closing, so its lists must refetch too: the
    // hires move candidates into Hired, and closing cancels open interviews.
    if (postingId) invalidatePostingLists(queryClient, postingId);
  };

  // Prevent back navigation to creation routes
  useBlockBackNavigation({
    blockedRoutePatterns: BLOCKED_ROUTE_PATTERNS,
    redirectTo: '/',
  });

  const backButton = (
    <BackButton url={fromOnsiteInterviewCreation ? '/permanent-jobs/postings' : undefined} />
  );

  // Posting Details' header arrow goes where the legacy page's BackButton did.
  const goBack = () =>
    fromOnsiteInterviewCreation ? navigate('/permanent-jobs/postings') : navigate(-1);

  // The Edit page pops back to the Posting Details entry that opened it, so that page's
  // own back arrow still leaves for wherever the office came from. Opened any other way
  // (a deep link), it swaps itself for the Candidates tab instead of stacking on top.
  const leaveEditPosting = () =>
    fromPostingDetails
      ? navigate(-1)
      : navigate(`/permanent-jobs/job-overview/${postingId}/${TAB.CANDIDATES}`, { replace: true });

  if (isLoading && isPostingDetails) {
    return (
      <DetailsPageFrame testID="posting-details-loading">
        <Center py={12} className="w-full">
          <CenteredSpinner />
        </Center>
      </DetailsPageFrame>
    );
  }

  if (isLoading) {
    return (
      <NoScreen>
        <Screen.Header title="Job Overview" start={backButton} />
        <Center style={{ marginBlock: 48 }}>
          <CenteredSpinner />
        </Center>
      </NoScreen>
    );
  }

  // The Close Job flow belongs to the route, not the layout: both arms open the same popups.
  const closeJobPopups = (
    <>
      {closeJobStep === 'select-candidates' && (
        <SelectCandidatesPopup
          jobPostingId={postingId || ''}
          onClose={endCloseJobFlow}
          onConfirm={handleSelectCandidates}
          defaultSelectedCandidateIds={selectedCandidateIds}
        />
      )}

      {closeJobStep === 'confirm-close' && (
        <ConfirmCloseJobPopup
          onGoBack={handleGoBack}
          onConfirm={handleConfirmClose}
          hasScheduledOnsiteInterviews={hasCandidateScheduledOnsiteInterviews}
          isLoading={isClosingJob}
        />
      )}

      {closeJobStep === 'feedback' && (
        <CloseJobFeedbackPopup onClose={handleFeedbackSkip} onSubmit={handleFeedbackSubmit} />
      )}

      {closeJobStep === 'success' && (
        <CloseJobSuccessPopup onClose={handleCloseJobSuccess} hasHires={hasHires} />
      )}
    </>
  );

  // With the flag on, "Edit posting" opens the existing Job Details sections on their
  // own page — the tab they lived in is gone.
  const renderPostingDetails = () =>
    activeTab === TAB.JOB_DETAILS ? (
      <NoScreen>
        <Screen.Header
          title={POSTING_DETAILS_COPY.editPosting}
          start={<BackButton onClick={leaveEditPosting} />}
        />
        <Column width="100%" data-cy="edit-posting">
          <JobDetailsTabView jobPosting={jobPosting} />
        </Column>
      </NoScreen>
    ) : (
      <PostingDetails
        postingId={postingId || ''}
        jobPosting={jobPosting}
        professionDisplay={professionDisplay || ''}
        activeTab={activeTab}
        onChangeTab={onChangeTab}
        onClosePosting={startCloseJobFlow}
        onBack={goBack}
        hasScheduledOnsiteInterviews={hasScheduledOnsiteInterviews}
      />
    );

  if (isPostingDetails) {
    return (
      <>
        {renderPostingDetails()}
        {closeJobPopups}
      </>
    );
  }

  return (
    <>
      <NoScreen>
        <Screen.Header title="Job Overview" start={backButton} />
        <Column gap={4} width="100%">
          <JobOverviewHeader
            closedAt={jobPosting?.closedAt ? new Date(jobPosting?.closedAt) : undefined}
            hasScheduledOnsiteInterviews={hasScheduledOnsiteInterviews}
            id={postingId || ''}
            officeName={jobPosting?.officeName || ''}
            postedAt={jobPosting?.publishedAt ? new Date(jobPosting?.publishedAt) : undefined}
            professional={professionDisplay || ''}
            status={jobPosting?.status as JobPostingStatus}
            suspendedAt={jobPosting?.suspendedAt ? new Date(jobPosting?.suspendedAt) : undefined}
            officeId={jobPosting?.officeId || undefined}
            officePlanId={jobPosting?.officePlanId || undefined}
            title={title || ''}
            onCloseJob={startCloseJobFlow}
          />
          <Tabs
            defaultValue={TAB.CANDIDATES}
            value={activeTab}
            onChange={onChangeTab}
            style={{ gap: 16, alignItems: 'center' }}
          >
            <Tab
              value={TAB.CANDIDATES}
              data-cy="candidates-tab"
              style={{ width: 'auto', minWidth: 'max-content' }}
            >
              Candidates
            </Tab>
            <Tab
              value={TAB.JOB_DETAILS}
              data-cy="job-details-tab"
              style={{ width: 'auto', minWidth: 'max-content' }}
            >
              Job Details
            </Tab>
            <Tab
              value={TAB.WORKING_INTERVIEWS}
              data-cy="onsite-interviews-tab"
              style={{ width: 'auto', minWidth: 'max-content' }}
            >
              Onsite Interviews
            </Tab>
            <TabPanel value={TAB.CANDIDATES} style={{ width: '100%' }}>
              <CandidateTabView
                jobPostingId={postingId || ''}
                officeId={jobPosting?.officeId || ''}
              />
            </TabPanel>
            <TabPanel value={TAB.JOB_DETAILS} style={{ width: '100%' }}>
              <JobDetailsTabView jobPosting={jobPosting} />
            </TabPanel>
            <TabPanel value={TAB.WORKING_INTERVIEWS} style={{ width: '100%' }}>
              <OnsiteInterviewTabView jobPostingId={postingId || ''} />
            </TabPanel>
          </Tabs>
        </Column>
      </NoScreen>

      {closeJobPopups}
    </>
  );
};
